import React, {useState} from 'react';
import {navigate} from '@reach/router';
import {Col, Dropdown, Row} from 'react-bootstrap';
import {Tooltip} from '@/components/tooltips';
import Button from '@/components/ui/Button';
import {Icon} from '@/components/icons/Icon';
import PaginationComponent from '@/components/pagination/PaginationComponent';
import useOptimizedConfig from '@/hooks/useOptimizedConfig';
import HelpTextBlock from '@/components/help_text/HelpTextBlock';
import LoadingWrapper from '@/components/loading/LoadingWrapper';
import {buildAdminChannelEvaluationTasksURL} from '@/utils/urlBuilder';
import TableComponent from '@/components/table/Table';
import {useFormattedDateTime} from '@/components/utils/FormattedDateTime';
import ChannelSettingsModal from './ChannelSettingsModal';
import ClickableTableCell from '@/components/table/ClickableTableCell';
import AwesomeDebouncePromise from 'awesome-debounce-promise';
import usePaginatedData from '@/utils/usePaginatedData';
import Form from 'react-bootstrap/Form';
import i18n from 'i18n';

import {hasPermission} from '@/utils/permissions/permissionDebug';

interface Channel {
    id: number;
    name: string;
    shortname: string;
    eventType: {
        name: string;
    };
    updatedOn: string;
    lastUpdated?: string;
    createdOn: string;
    numberOfEvaluationTasks?: number;
    active: boolean;
}

interface PaginatedDataResult<T> {
    data: T[];
    loading: boolean;
    currentPage: number | null;
    setCurrentPage: (page: number) => void;
    pages: number[] | null;
}

const ChannelListView: React.FC = React.memo(() => {
    const {formatDateTime, isLoaded: configLoaded} = useFormattedDateTime();

    const setSearchQueryState = async (
        setSearchQuery: (value: string) => void,
        value: string,
        setPage: (page: number) => void
    ): Promise<void> => {
        setPage(1);
        setSearchQuery(value);
    };

    const setSearchQueryStateDebounced = AwesomeDebouncePromise(setSearchQueryState, 350);
    const [searchQuery, setSearchQuery] = useState<string>('');
    const {userData, loading: loadingUserData} = useOptimizedConfig();

    const [settingsModalOpen, setSettingsModalOpen] = useState<boolean>(false);
    const [selectedChannelId, setChannelId] = useState<number>(0);

    const userCanAccessChannelsResource = hasPermission('fs.channel.list', userData?.permissions, true);
    const userCanCreateChannel = hasPermission('fs.channel.create', userData?.permissions, true);
    const userCanViewChannelEvaluationTasks = hasPermission('fs.channel.evaluation_task.list', userData?.permissions, true);
    const userCanChangeChannel = hasPermission('fs.channel.update', userData?.permissions, true);

    const {
        data: channels,
        loading,
        currentPage,
        setCurrentPage,
        pages,
    }: PaginatedDataResult<Channel> = usePaginatedData(window.URLS.channelsResource, {
        storeResolver: 'channels',
        requestParams: {
            params: {
                query: searchQuery,
            },
        },
    });

    const inputChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        setSearchQueryStateDebounced(setSearchQuery, event.target.value, setCurrentPage);
    };

    const active = currentPage;
    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };

    if (!userCanAccessChannelsResource && !loadingUserData) {
        return (
            <>
                <HelpTextBlock helpText={i18n.t('ht_the_channel.text')} />

                <div className="alert alert-danger" role="alert"> {i18n.t('tt_you_dont_have_permission_to_access_the_requested_resource.text')}
                </div>
            </>
        );
    }

    const openSettingsModal = (channelId: number): void => {
        setChannelId(channelId);
        setSettingsModalOpen(true);
    };

    const closeSettingsModal = (): void => {
        setSettingsModalOpen(false);
        setChannelId(0);
    };

    const detailUrlGenerator = (rowData: Channel): string => {
        return buildAdminChannelEvaluationTasksURL(rowData.id);
    };

    const columns = [
        {
            accessorKey: 'name',
            header: i18n.t('th_name.text'),
            cell: (cell: any) => (
                <ClickableTableCell cell={cell} urlGenerator={detailUrlGenerator} />
            ),
        },
        {
            accessorKey: 'shortname',
            header: i18n.t('th_shortname.text'),
        },
        {
            accessorKey: 'eventType.name',
            header: i18n.t('th_event_type.text'),
        },
        {
            accessorKey: 'updatedOn',
            header: i18n.t('th_last_modified.text'),
            accessorFn: (cell: Channel) => {
                if (!configLoaded) { return ''; }

                if (!cell.lastUpdated || cell.lastUpdated === '-') {
                    return formatDateTime(cell.createdOn, true, {setZone: false});
                }

                return formatDateTime(cell.lastUpdated, true, {setZone: false});
            },
        },
        {
            accessorKey: 'numberOfEvaluationTasks',
            header: i18n.t('th_evaluation_tasks.text'),
            accessorFn: (cell: Channel) => cell.numberOfEvaluationTasks || 0,
        },
        {
            accessorKey: 'active',
            header: i18n.t('th_action.text'),
            cell: (cell: any) => {
                if (cell.getValue() === true) {
                    return (<Icon name="mdi-check" className="text-success" />);
                } else {
                    return (<Icon name="mdi-close" className="text-danger" />);
                }
            },
        },
        {
            header: i18n.t('th_action.text'),
            cell: (cell: any) => {
                return (
                    <div className="d-flex align-items-center justify-content-end w-100">
                        <div className="d-inline-block mr-2">
                            {
                                userCanViewChannelEvaluationTasks ? (
                                    <Tooltip content={i18n.t('jt_view.text')} placement="top">
                                        <Button
                                            variant="outline-info"
                                            size="sm"
                                            icon="mdi-eye"
                                            onClick={() => navigate(detailUrlGenerator(cell.row.original))}
                                            data-cy="channel-view-btn"
                                        />
                                    </Tooltip>
                                ) : (
                                    <Tooltip
                                        content={i18n.t('tt_you_dont_have_permission_to_access_the_requested_resource.text')}
                                        placement="top"
                                    >
                                        <div style={{
                                            display: 'inline-block',
                                            cursor: 'not-allowed',
                                        }}
                                        >
                                            <Button
                                                variant="outline-secondary"
                                                size="sm"
                                                icon="mdi-eye"
                                                disabled
                                                style={{pointerEvents: 'none'}}
                                                data-cy="channel-list-eye-outlinesecondary-btn" />
                                        </div>
                                    </Tooltip>
                                )
                            }
                        </div>
                        <Tooltip content={i18n.t('jt_actions.text')} placement="top">
                            <Dropdown className="d-inline-block">
                                <Dropdown.Toggle
                                    as={Button}
                                    variant="outline-info"
                                    size="sm"
                                    icon="mdi-dots-horizontal"
                                    className="dropdown-toggle-no-caret"
                                    data-cy="channel-settings-toggle"
                                    id="settings-dropdown-1"
                                />

                                <Dropdown.Menu align="end">
                                    {
                                        userCanChangeChannel ? (
                                            <Dropdown.Item
                                                onClick={() => openSettingsModal(cell.row.original.id)}
                                                data-cy="channel-settings-btn"
                                            >
                                                <Icon name="mdi-settings" className="mr-2" />  {i18n.t('jt_settings.text')}
                                            </Dropdown.Item>
                                        ) : (
                                            <Tooltip
                                                content={i18n.t('tt_you_dont_have_permission_to_access_the_requested_resource.text')}
                                                placement="top"
                                            >
                                                <div
                                                    className="ml-auto"
                                                    style={{
                                                        cursor: 'not-allowed',
                                                        display: 'flex',
                                                    }}
                                                >
                                                    <Dropdown.Item
                                                        style={{pointerEvents: 'none'}}
                                                        disabled
                                                        data-cy="channel-list-btn">
                                                        <Icon name="mdi-settings" className="mr-2" /> {i18n.t('jt_settings.text')}
                                                    </Dropdown.Item>
                                                </div>
                                            </Tooltip>
                                        )
                                    }
                                </Dropdown.Menu>
                            </Dropdown >
                        </Tooltip>
                    </div>
                );
            },
        },
    ];

    return (
        <>
            <HelpTextBlock helpText={i18n.t('ht_the_channel.text')} />

            <Row>
                <Col lg={3}>
                    <Form className="mb-2 d-flex align-items-center search-box" onSubmit={(e: React.FormEvent) => {
                        e.preventDefault();
                    }}>
                        <Form.Control
                            type="text"
                            placeholder={i18n.t('es_search.text')}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => inputChange(event)}
                            data-cy="eval-search-field"
                        />
                    </Form>
                </Col>
                <Col lg={9} className="d-flex justify-content-end align-items-center">
                    {
                        userCanCreateChannel ? (
                            <Button
                                variant="info"
                                size="sm"
                                className="mb-2"
                                icon="mdi-plus"
                                onClick={() => setSettingsModalOpen(true)}
                                data-cy="new-channel-btn"
                            >
                                {i18n.t('jt_add_new_channel.text')}
                            </Button>
                        ) : (
                            <Tooltip
                                content={i18n.t('tt_you_dont_have_permission_to_access_the_requested_resource.text')}
                                placement="top"
                            >
                                <div style={{cursor: 'not-allowed', display: 'flex', alignItems: 'center'}}>
                                    <Button
                                        variant="secondary"
                                        size="sm"
                                        className="mb-2"
                                        icon="mdi-plus"
                                        disabled
                                        style={{pointerEvents: 'none'}}
                                        data-cy="channel-list-plus-btn">
                                        {i18n.t('jt_add_new_channel.text')}
                                    </Button>
                                </div>
                            </Tooltip>
                        )
                    }
                </Col>
            </Row>

            {
                loading ? (
                    <LoadingWrapper loading={true} loadingText={i18n.t('jt_loading_data_general.text')} minHeight="400px"><div /></LoadingWrapper>
                ) : (
                    <>
                        <div className="card">
                            <div className="card-body">
                                    <TableComponent data={channels} columns={columns} />
                                    <PaginationComponent
                                        pages={pages}
                                        currentPage={currentPage}
                                        onPageChange={handlePageChange}
                                    />
                            </div>
                        </div>
                        {settingsModalOpen && (
                            <ChannelSettingsModal
                                modalOpen={settingsModalOpen}
                                closeModal={closeSettingsModal}
                                activeChannel={selectedChannelId}
                            />
                        )}
                    </>
                )
            }

        </>
    );
});

export default ChannelListView; 